import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';

import {assert} from 'Asserts';
import BaseEditableField from 'ui-library/BaseEditableField';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import WarningIcon from 'ui-library/icons/WarningIcon';
import DateField from 'ui-library/inputs/DateField';
import PopoverIcon, {PopoverIconProps} from 'ui-library/PopoverIcon';
import TextValue from 'ui-library/TextValue';
import {readableTime} from 'util/DateUtil';

import DatetimeField from './inputs/DatetimeField';

export interface EditableDateFieldProps {
  isDisabled: boolean;
  onChange: (value: string | null) => void;
  /** If non-empty, a valid initial date/datetime value to display. May be reformatted in UI. */
  value: string | null;
  placeholder?: string | null;
  /* Props for icon to render in view mode next to the date. */
  viewerPopoverIconProps?: PopoverIconProps;
  /* If provided, wait for confirmation from this function before entering edit mode */
  confirmBeforeStartEditing?: () => Promise<boolean>;
  /* Disable and render provided tooltip if non-null */
  disableEditsWithTooltip?: string | null;
  shouldShowWarningIcon?: boolean;
  pickerType: 'date' | 'datetime';
  /**
   * Valid formats for the input date/datetime field. If specified, the caller must provide formats matching
   * the picker type. These will be parsed by Moment.js: details in
   * https://momentjs.com/docs/#/parsing/string-format/. Dates/datetimes not conforming to any of these
   * formats will display with error styling, but that does not block submitting the edit.
   */
  validFormats?: Array<string>;
  /**
   * When editing, preexisting or saved values will convert to this format, by default. If specified, this
   * should be a valid Moment.js format: details in https://momentjs.com/docs/#/parsing/string-format/. This
   * does not affect the read view: that will conform to the user preferences or default for their locale.
   */
  editPresentationalFormat?: string;
}

interface State {
  editingValue: string | null;
  isBeingEdited: boolean;
}

export default class EditableDateField extends React.Component<EditableDateFieldProps, State> {
  static defaultProps = {
    isDisabled: false,
    pickerType: 'date',
  };

  state: State = {
    editingValue: this.props.value,
    isBeingEdited: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: EditableDateFieldProps): void {
    if (!this.state.isBeingEdited) {
      this.setState({editingValue: nextProps.value});
    }
  }

  private handleChangeValue = (value: string | null): void => {
    this.setState({editingValue: value});
  };

  private handleCancel = (): void => {
    this.setState({
      editingValue: this.props.value,
      isBeingEdited: false,
    });
  };

  private handleComplete = (): void => {
    this.props.onChange(this.state.editingValue);
    this.setState({
      editingValue: this.props.value,
      isBeingEdited: false,
    });
  };

  private formatValueForInitialEdit(): string | null {
    if (this.props.value == null || this.props.value === '') {
      return null;
    }
    const momentValue = moment(this.props.value);
    if (momentValue.isValid() && this.props.editPresentationalFormat) {
      return momentValue.format(this.props.editPresentationalFormat);
    }
    return this.props.value;
  }

  private handleEdit = (): void => {
    this.setState({editingValue: this.formatValueForInitialEdit(), isBeingEdited: true});
  };

  private handleEditWithConfirmation = async (): Promise<void> => {
    assert(this.props.confirmBeforeStartEditing != null, 'Expected confirmBeforeStartEditing to be provided');
    const shouldProceed = await this.props.confirmBeforeStartEditing();
    if (shouldProceed) {
      this.setState({editingValue: this.formatValueForInitialEdit(), isBeingEdited: true});
    }
  };

  render(): JSX.Element {
    const isDisabled = this.props.isDisabled || this.props.disableEditsWithTooltip != null;
    const pickerType = this.props.pickerType;
    const editableField = (
      <BaseEditableField
        editor={
          pickerType === 'date' ? (
            <DateField
              autoFocus={false}
              labelStyle={DateField.LABEL_STYLES.HIDDEN}
              placeholder="Date"
              onChange={this.handleChangeValue}
              value={this.state.editingValue}
              validDatetimeInputFormats={this.props.validFormats}
              datetimePresentationalFormat={this.props.editPresentationalFormat}
            />
          ) : (
            <DatetimeField
              autoFocus={false}
              labelStyle={DateField.LABEL_STYLES.HIDDEN}
              placeholder="Date and time"
              onChange={this.handleChangeValue}
              value={this.state.editingValue}
              validDatetimeInputFormats={this.props.validFormats}
              datetimePresentationalFormat={this.props.editPresentationalFormat}
            />
          )
        }
        isBeingEdited={this.state.isBeingEdited}
        isCompleteEnabled={true}
        isWritable={!isDisabled}
        viewer={
          <>
            {this.props.shouldShowWarningIcon && (
              <WarningIcon className={classNames(css(styles.warningIcon))} />
            )}
            <span className={css(styles.viewerWrapper)}>
              <TextValue
                value={
                  this.props.value
                    ? readableTime(this.props.value, {
                        showTime: pickerType === 'datetime',
                      })
                    : ''
                }
                placeholder={this.props.placeholder}
              />
              {this.props.value != null && this.props.viewerPopoverIconProps != null && (
                <PopoverIcon {...this.props.viewerPopoverIconProps} />
              )}
            </span>
          </>
        }
        onCancel={this.handleCancel}
        onComplete={this.handleComplete}
        onEdit={
          this.props.confirmBeforeStartEditing != null ? this.handleEditWithConfirmation : this.handleEdit
        }
      />
    );
    return (
      <Tooltip
        text={this.props.disableEditsWithTooltip}
        isDisabled={!this.props.disableEditsWithTooltip}
        // Tooltip displayed below the field if another popover could be present to avoid conflicts.
        placement={this.props.viewerPopoverIconProps != null ? FloatingPlacement.BOTTOM : undefined}
      >
        <div>{editableField}</div>
      </Tooltip>
    );
  }
}

const styles = StyleSheet.create({
  viewerWrapper: {
    display: 'flex',
  },
  warningIcon: {
    marginRight: '5px',
  },
});
